import { rest } from 'msw';

import { components as billing } from 'interfaces/api/billing';
import { DistributedTransaction } from 'interfaces/ApiTypeAliases';
import { getConfig } from 'shared/config';

import {
  billingAccounts,
  creditNotes,
  invoiceDetails,
  invoiceDetailsOnlyOnetime,
  invoiceDetailsOnlySubscriptions,
  invoices,
  publicInvoice,
  redeemCreditNoteResponseMock,
  thirdPartyBillingAccounts,
  validateCreditNoteResponseMock,
} from '.';

// needs to be 9 digits
const terminatedAccountNumber = 111222333;
/** use this locally if you want to test terminated invoices, don't commit it to be `true` */
const forceAllInvoicesToBeTerminated = false;

const odinAPI = getConfig().odin?.host;
const billingAPI = `${getConfig().microServices?.host}/billing`;

type PayInvoiceRequest =
  billing['schemas']['Nuuday.NextGen.Billing.Contracts.Payment.CreateOneOffPayment'];

export const handlers = [
  rest.get(
    `${odinAPI}/yousee/selfcare/invoice/:accountNumber/details/:invoiceNumber`,
    (req, res, ctx) => {
      const invoiceNr = req.params.invoiceNumber;

      // the 2nd and 3rd invoice in the list will only have one of the sections
      if (invoiceNr === '2')
        return res(ctx.json({ payload: { invoiceDetails: invoiceDetailsOnlyOnetime } }));
      if (invoiceNr === '3')
        return res(ctx.json({ payload: { invoiceDetails: invoiceDetailsOnlySubscriptions } }));

      return res(ctx.json({ payload: { invoiceDetails } }));
    },
  ),
  rest.post(
    `${odinAPI}/yousee/accounts/:accountNumber/invoices/:statementNumber/validatecreditnote`,
    (req, res, ctx) => {
      return res(ctx.json(validateCreditNoteResponseMock));
    },
  ),
  rest.post(
    `${odinAPI}/yousee/accounts/:accountNumber/invoices/:statementNumber/refundpublic`,
    (req, res, ctx) => {
      return res(ctx.json(redeemCreditNoteResponseMock));
    },
  ),
  rest.post<PayInvoiceRequest>(
    `${billingAPI}/users/:userId/invoices/:statementId/payments/creditcard`,
    (req, res, ctx) => {
      const result = {
        merchantId: '90197447',
        currencyCode: 'DKK',
        formattedAmount: '5000',
        mitType: 'UCOF',
        paymentGatewayAcceptedCallback:
          'https://api.non-prod.mit-yousee.yousee.dk/payment/gateway/netsD2/callback/OneOffPaymentAndAgreement',
        paymentId: 1198,
        externalPaymentId: 'SSC-204042112-4-1198',
        redirectUri: 'https://payment.architrade.com/paymentweb/start.action',
        acceptedUserRedirect:
          'https://api.non-prod.mit-yousee.yousee.dk/payment/gateway/oneoffpayments/pending/1198/redirect/Accepted',
        rejectedUserRedirect:
          'https://api.non-prod.mit-yousee.yousee.dk/payment/gateway/oneoffpayments/pending/1198/redirect/Rejected',
      };
      return res(ctx.json(result));
    },
  ),
  rest.get(`${billingAPI}/my/associations/thirdparty`, (req, res, ctx) => {
    return res(ctx.json(thirdPartyBillingAccounts));
  }),

  rest.post(`${billingAPI}/creditnotes/:statementId/refund`, (req, res, ctx) => {
    const transaction: DistributedTransaction = {
      id: '42',
    };
    return res(ctx.json(transaction));
  }),

  rest.get(`${billingAPI}/my`, (req, res, ctx) => {
    if (forceAllInvoicesToBeTerminated && req.url.searchParams.get('includeTerminatedAccounts')) {
      return res(
        ctx.json([
          ...billingAccounts,
          { accountState: 'Terminated', accountNumber: terminatedAccountNumber },
        ]),
      );
    }

    return res(ctx.json(billingAccounts));
  }),
  rest.get(`${billingAPI}/users/:userId/statements/grouped`, (req, res, ctx) => {
    const numberOfMonths = Number(new URLSearchParams(req.url.search).get('numberOfMonths')) ?? 12;
    // filter invoices and creditNotes using `numberOfMonths` to mimic the backend
    const monthsAgo = (months: number) => {
      const now = new Date();
      return new Date(now.setMonth(now.getMonth() - months));
    };

    const statements = invoices;

    // set all acc numbers to terminated
    if (forceAllInvoicesToBeTerminated && req.url.searchParams.get('includeTerminatedAccounts')) {
      statements.forEach((x) => (x.billingAccountNumber = terminatedAccountNumber));
    }

    const filteredInvoices = statements.filter(
      (invoice) => new Date(invoice.createdAtDate as string) > monthsAgo(numberOfMonths),
    );

    // uncomment to apply filtering to credit notes. See YOHS-1467 for more info
    // const filteredCreditNotes = creditNotes.filter(
    //   (creditNote) => new Date(creditNote.createdAtDate) > monthsAgo(numberOfMonths),
    // );

    // creditNote is not a typo, it's how it's on the backend
    return res(ctx.json({ invoices: filteredInvoices, creditNote: creditNotes }));
  }),

  rest.get(`${billingAPI}/users/:userId/statements`, (req, res, ctx) => {
    // filter invoices and creditNotes using `numberOfMonths` to mimic the backend
    const numberOfMonths = Number(new URLSearchParams(req.url.search).get('numberOfMonths')) ?? 12;
    const monthsAgo = (months: number) => {
      const now = new Date();
      return new Date(now.setMonth(now.getMonth() - months));
    };

    const filteredInvoices = invoices.filter(
      (invoice) => new Date(invoice.createdAtDate as string) > monthsAgo(numberOfMonths),
    );
    const filteredCreditNotes = creditNotes.filter(
      (creditNote) => new Date(creditNote.createdAtDate as string) > monthsAgo(numberOfMonths),
    );
    return res(ctx.json([...filteredCreditNotes, ...filteredInvoices]));
  }),
  rest.put(`${billingAPI}/:accountNumber/notificationsettings`, (req, res, ctx) => {
    return res(ctx.json({}));
  }),
  rest.put(`${billingAPI}/:accountNumber/communicationsettings`, (req, res, ctx) => {
    return res(ctx.json({}));
  }),
  rest.get(
    `${billingAPI}/public/accounts/:accountNumber/invoice/:statementNumber`,
    (req, res, ctx) => {
      return res(ctx.json(publicInvoice));
    },
  ),
  /**
   * This is a mock for the payment gateway. It returns a redirect url to the payment gateway.
   * But for the sake of testing it with mocked data, we just return the url we will eventually
   * receive from the backend. Uncomment this to test the payment flow with mocked data. But be
   * aware that redirect will go to the same page as you are on and will not show the page as
   * intended. The page as intended can be seen in storybook.
   */
  // rest.post(
  //   `${billingAPI}/public/accounts/:accountNumber/invoices/:statementNumber/payments/creditcard`,
  //   (req, res, ctx) => {
  //     return res(
  //       ctx.json({
  //         agreementId: 1,
  //         externalAgreementId: '123456789',
  //         redirectUri: (req.body as any).acceptedUserRedirect,
  //       }),
  //     );
  //   },
  // ),
];
